import { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import {
  ChartBarIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  XMarkIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { peerBenchmarkingApi, callReportApi } from '../lib/api';
import FeatureGate from '../components/FeatureGate';
import clsx from 'clsx';
import { PageHeader, LoadingSpinner, StatusBadge } from '../components/ui';
import { useTeamId } from '../hooks/useTeamId';
import FindPeersPanel from '../components/peer/FindPeersPanel';

const ASSET_BUCKETS = [
  { value: 'SMALL', label: '<$412 million' },
  { value: 'INTERMEDIATE', label: '$412 million to <$1.649 billion' },
  { value: 'LARGE', label: '$1.649 billion +' },
];

const TABS = [
  { id: 'hmda' as const, label: 'HMDA Metrics (Market Peers)' },
  { id: 'financial' as const, label: 'Financial Metrics (CRA Peers)' },
];

type TabId = typeof TABS[number]['id'];

// Guarded formatters — render "—" instead of "undefined%" / "$NaNM" when a metric is missing.
const fmtPct = (v: number | null | undefined, n = 1) => (v == null ? '—' : `${v.toFixed(n)}%`);
const fmtUsdM = (thousands: number | null | undefined) => (thousands == null ? '—' : `$${(thousands / 1000).toFixed(0)}M`);

const DeltaIndicator = ({ value, invert }: { value: number; invert?: boolean }) => {
  const positive = invert ? value < 0 : value > 0;
  const negative = invert ? value > 0 : value < 0;
  if (positive) return <span className="inline-flex items-center text-red-600"><ArrowUpIcon className="h-3 w-3 mr-0.5" />+{Math.abs(value).toFixed(1)}%</span>;
  if (negative) return <span className="inline-flex items-center text-green-600"><ArrowDownIcon className="h-3 w-3 mr-0.5" />{Math.abs(value).toFixed(1)}%</span>;
  return <span className="text-gray-400">—</span>;
};

const MetricDelta = ({ value, peer, format, suffix = '', invert }: {
  value?: number | null; peer?: number | null; format?: 'pct' | 'dollar' | 'ratio'; suffix?: string; invert?: boolean;
}) => {
  if (value == null || peer == null) return <span className="text-gray-400">—</span>;
  const delta = value - peer;
  const isGood = invert ? delta < 0 : delta > 0;
  const isBad = invert ? delta > 0 : delta < 0;
  return (
    <span className={clsx('text-xs font-medium', isGood && 'text-green-600', isBad && 'text-red-600', !isGood && !isBad && 'text-gray-400')}>
      {delta > 0 ? '+' : ''}{format === 'dollar' ? `$${delta.toLocaleString()}` : `${delta.toFixed(2)}${suffix}`}
    </span>
  );
};

export default function PeerBenchmarkingPage() {
  const queryClient = useQueryClient();
  const [showConfig, setShowConfig] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('hmda');

  // Team
  const { teamId } = useTeamId();

  // HMDA Config
  const { data: configData, isLoading: configLoading } = useQuery({
    queryKey: ['peerConfig', teamId],
    queryFn: () => peerBenchmarkingApi.getConfig(teamId!),
    enabled: !!teamId,
  });
  const config = configData?.data;
  const hasConfig = config && config.msaCodes?.length > 0;

  // HMDA Dashboard (only if configured)
  const { data: dashData, isLoading: dashLoading } = useQuery({
    queryKey: ['peerDashboard', teamId],
    queryFn: () => peerBenchmarkingApi.getDashboard(teamId!),
    enabled: !!teamId && !!hasConfig && activeTab === 'hmda',
  });
  const dash = dashData?.data;

  // Denial rates
  const { data: denialData } = useQuery({
    queryKey: ['peerDenialRates', teamId],
    queryFn: () => peerBenchmarkingApi.getDenialRates(teamId!),
    enabled: !!teamId && !!hasConfig && activeTab === 'hmda',
  });

  // Market share
  const { data: marketData } = useQuery({
    queryKey: ['peerMarketShare', teamId],
    queryFn: () => peerBenchmarkingApi.getMarketShare(teamId!),
    enabled: !!teamId && !!hasConfig && activeTab === 'hmda',
  });

  // Product mix
  const { data: productData } = useQuery({
    queryKey: ['peerProductMix', teamId],
    queryFn: () => peerBenchmarkingApi.getProductMix(teamId!),
    enabled: !!teamId && !!hasConfig && activeTab === 'hmda',
  });

  // Refresh (HMDA)
  const refreshMutation = useMutation({
    mutationFn: () => peerBenchmarkingApi.refresh(teamId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peerDashboard'] });
      queryClient.invalidateQueries({ queryKey: ['peerDenialRates'] });
      queryClient.invalidateQueries({ queryKey: ['peerMarketShare'] });
      queryClient.invalidateQueries({ queryKey: ['peerProductMix'] });
      toast.success('Data refresh started');
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Refresh failed'),
  });

  return (
    <FeatureGate feature="peer_benchmarking" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          title="Peer Benchmarking"
          description="Compare your institution against peers using public CFPB and FDIC data"
          actions={
            activeTab === 'hmda' && hasConfig ? (
              <div className="flex gap-2">
                <button
                  onClick={() => setShowConfig(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Cog6ToothIcon className="h-4 w-4" />
                  Configure
                </button>
                <button
                  onClick={() => refreshMutation.mutate()}
                  disabled={refreshMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                >
                  <ArrowPathIcon className={clsx('h-4 w-4', refreshMutation.isPending && 'animate-spin')} />
                  Refresh Data
                </button>
              </div>
            ) : undefined
          }
        />

        {/* Tab Bar */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex gap-6" aria-label="Tabs">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  'whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* HMDA Tab */}
        {activeTab === 'hmda' && (
          <>
            {configLoading || dashLoading ? (
              <LoadingSpinner size="md" className="py-12" />
            ) : !hasConfig ? (
              <SetupForm teamId={teamId} onComplete={() => {
                queryClient.invalidateQueries({ queryKey: ['peerConfig'] });
              }} />
            ) : (
              <>
                {/* What "peers" means on this tab (distinct from the FDIC CRA peer group on Financial Metrics) */}
                <p className="text-xs text-gray-500">
                  <span className="font-medium">HMDA market peers</span> — these comparisons are against all lenders in your
                  assessment areas (CFPB HMDA aggregate), used for fair-lending denial-rate and market-share analysis. This is
                  <span className="font-medium"> not</span> the same as the named, asset-comparable <span className="font-medium">CRA Peer Group</span>
                  {' '}on the Financial Metrics tab.
                </p>

                {/* Peer group description */}
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>Institution: <strong className="text-gray-900">{config.institutionName || config.institutionLei || 'Not set'}</strong></span>
                  <span>MSAs: <strong className="text-gray-900">{config.msaCodes?.join(', ') || 'None'}</strong></span>
                  <span>CRA Asset Size: <strong className="text-gray-900">{ASSET_BUCKETS.find(b => b.value === config.assetSizeBucket)?.label || config.assetSizeBucket || 'Not set'}</strong></span>
                </div>

                {/* Summary cards */}
                {dash && (
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <div className="rounded-lg border bg-white p-4">
                      <p className="text-xs font-medium text-gray-500">Your Denial Rate</p>
                      <p className="mt-1 text-2xl font-bold text-gray-900">{dash.institution?.denialRate?.toFixed(1) || '—'}%</p>
                      {dash.comparison?.denialRateDelta != null && (
                        <p className="mt-0.5 text-xs"><DeltaIndicator value={dash.comparison.denialRateDelta} /> vs peers</p>
                      )}
                    </div>
                    <div className="rounded-lg border bg-white p-4">
                      <p className="text-xs font-medium text-gray-500">Peer Avg Denial Rate</p>
                      <p className="mt-1 text-2xl font-bold text-gray-900">{dash.peerGroup?.avgDenialRate?.toFixed(1) || '—'}%</p>
                      <p className="mt-0.5 text-xs text-gray-400">{dash.peerGroup?.peerCount || 0} peers</p>
                    </div>
                    <div className="rounded-lg border bg-white p-4">
                      <p className="text-xs font-medium text-gray-500">Black/White Ratio</p>
                      <p className="mt-1 text-2xl font-bold text-gray-900">{dash.institution?.ratios?.blackWhite?.toFixed(2) || '—'}</p>
                      {dash.peerGroup?.avgRatios?.blackWhite && (
                        <p className="mt-0.5 text-xs text-gray-400">Peer avg: {dash.peerGroup.avgRatios.blackWhite.toFixed(2)}</p>
                      )}
                    </div>
                    <div className="rounded-lg border bg-white p-4">
                      <p className="text-xs font-medium text-gray-500">Risk Flags</p>
                      <p className={clsx('mt-1 text-2xl font-bold', (dash.comparison?.riskFlags?.length || 0) > 0 ? 'text-red-600' : 'text-green-600')}>
                        {dash.comparison?.riskFlags?.length || 0}
                      </p>
                    </div>
                  </div>
                )}

                {/* Risk flags */}
                {dash?.comparison?.riskFlags?.length > 0 && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                    <h3 className="text-sm font-semibold text-red-800">Risk Flags</h3>
                    <ul className="mt-2 list-disc list-inside space-y-1">
                      {dash.comparison.riskFlags.map((flag: string, i: number) => (
                        <li key={i} className="text-sm text-red-700">{flag}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Denial Rate Comparison */}
                {denialData?.data && (
                  <div className="rounded-lg border bg-white">
                    <div className="border-b px-4 py-3">
                      <h3 className="text-sm font-semibold text-gray-900">Denial Rate Comparison by Demographic</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Group</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Your Rate</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Peer Avg</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Delta</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Your Ratio</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Peer Ratio</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Risk</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {(denialData.data.groups || []).map((g: any) => (
                            <tr key={g.group} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{g.group}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{g.yourRate?.toFixed(1)}%</td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{g.peerRate?.toFixed(1)}%</td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm"><DeltaIndicator value={g.delta || 0} /></td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{g.yourRatio?.toFixed(2) || '—'}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{g.peerRatio?.toFixed(2) || '—'}</td>
                              <td className="whitespace-nowrap px-4 py-3">
                                <StatusBadge variant={
                                  g.riskLevel === 'HIGH' ? 'danger' :
                                  g.riskLevel === 'MEDIUM' ? 'warning' :
                                  'success'
                                }>
                                  {g.riskLevel || 'LOW'}
                                </StatusBadge>
                              </td>
                            </tr>
                          ))}
                          {(!denialData.data.groups || denialData.data.groups.length === 0) && (
                            <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-400">No denial rate data available</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Market Share */}
                {marketData?.data?.markets && marketData.data.markets.length > 0 && (
                  <div className="rounded-lg border bg-white">
                    <div className="border-b px-4 py-3">
                      <h3 className="text-sm font-semibold text-gray-900">Market Share Analysis</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">MSA</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Your Originations</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Market Total</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Share %</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {marketData.data.markets.map((m: any) => (
                            <tr key={m.msaCode} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{m.msaName || m.msaCode}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{m.yourOriginations?.toLocaleString()}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{m.marketTotal?.toLocaleString()}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{m.sharePct?.toFixed(2)}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Product Mix */}
                {productData?.data?.products && productData.data.products.length > 0 && (
                  <div className="rounded-lg border bg-white">
                    <div className="border-b px-4 py-3">
                      <h3 className="text-sm font-semibold text-gray-900">Product Mix Comparison</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Product Type</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Your %</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Peer Avg %</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Delta</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {productData.data.products.map((p: any) => (
                            <tr key={p.type} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{p.type}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">{p.yourPct?.toFixed(1)}%</td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{p.peerPct?.toFixed(1)}%</td>
                              <td className="whitespace-nowrap px-4 py-3 text-sm"><DeltaIndicator value={p.delta || 0} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Data freshness */}
                {dash?.fetchedAt && (
                  <p className="text-xs text-gray-400">Data last refreshed: {new Date(dash.fetchedAt).toLocaleString()}</p>
                )}
              </>
            )}
          </>
        )}

        {/* Financial Metrics Tab */}
        {activeTab === 'financial' && teamId && (
          <FinancialMetricsTab teamId={teamId} />
        )}

        {/* Config Modal */}
        <ConfigModal
          isOpen={showConfig}
          onClose={() => setShowConfig(false)}
          teamId={teamId}
          existingConfig={config}
        />
      </div>
    </FeatureGate>
  );
}

// ─── Financial Metrics Tab ────────────────────────────────────────────────────

const PEER_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

function FinancialMetricsTab({ teamId }: { teamId: string }) {
  const queryClient = useQueryClient();

  // Call Report config
  const { data: crConfigData, isLoading: crConfigLoading } = useQuery({
    queryKey: ['callReportConfig', teamId],
    queryFn: () => callReportApi.getConfig(teamId),
  });
  const crConfig = crConfigData?.data;
  const hasCrConfig = crConfig && crConfig.fdicCertNumber;

  // Dashboard
  const { data: crDashData, isLoading: crDashLoading } = useQuery({
    queryKey: ['callReportDashboard', teamId],
    queryFn: () => callReportApi.getDashboard(teamId),
    enabled: !!hasCrConfig,
  });
  const crDash = crDashData?.data;

  // Trend
  const { data: crTrendData } = useQuery({
    queryKey: ['callReportTrend', teamId],
    queryFn: () => callReportApi.getTrend(teamId),
    enabled: !!hasCrConfig,
  });
  const crTrend = crTrendData?.data;

  // Refresh
  const refreshMutation = useMutation({
    mutationFn: () => callReportApi.refresh(teamId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callReportDashboard'] });
      queryClient.invalidateQueries({ queryKey: ['callReportTrend'] });
      toast.success('Call Report data refresh started');
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Refresh failed'),
  });

  if (crConfigLoading) return <LoadingSpinner size="md" className="py-12" />;

  if (!hasCrConfig) {
    return (
      <FdicSetupForm teamId={teamId} onComplete={() => {
        queryClient.invalidateQueries({ queryKey: ['callReportConfig'] });
      }} />
    );
  }

  const inst = crDash?.institution;
  const peer = crDash?.peerGroup;
  // Backend returns RiskFlag objects ({ metric, value, threshold, severity, message }), not strings —
  // rendering the object directly crashed the tab once Call Report data was present.
  const riskFlags: Array<{ metric: string; message: string; severity?: string; value?: number | null }> = crDash?.riskFlags || [];

  return (
    <div className="space-y-6">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-gray-600">
          <span>FDIC Cert: <strong className="text-gray-900">#{crConfig.fdicCertNumber}</strong></span>
          {/* Peer Group display hidden 2026-04-26 — Peer Asset Range tabled until Michael's reworked peer methodology lands. */}
        </div>
        <button
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
        >
          <ArrowPathIcon className={clsx('h-4 w-4', refreshMutation.isPending && 'animate-spin')} />
          Refresh from FDIC
        </button>
      </div>

      {/* Clarid-determined peer group via FDIC BankFind (Michael 2026-06-04) — replaces the
          manual asset-range entry that was hidden 2026-04-26. */}
      <FindPeersPanel cert={crConfig.fdicCertNumber} />

      {crDashLoading ? (
        <LoadingSpinner size="md" className="py-12" />
      ) : !inst ? (
        <div className="rounded-lg border bg-white p-8 text-center">
          <BanknotesIcon className="mx-auto h-12 w-12 text-gray-300" />
          <p className="mt-3 text-sm text-gray-500">No Call Report data yet. Click "Refresh from FDIC" to fetch your institution's data.</p>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <SummaryCard label="Total Assets" value={fmtUsdM(inst.totalAssets)} peer={peer ? fmtUsdM(peer.totalAssets) : undefined} />
            <SummaryCard label="Return on Assets" value={fmtPct(inst.returnOnAssets, 2)} peer={peer ? fmtPct(peer.returnOnAssets, 2) : undefined}
              delta={inst.returnOnAssets != null && peer?.returnOnAssets != null ? inst.returnOnAssets - peer.returnOnAssets : undefined} suffix="%" />
            <SummaryCard label="Net Interest Margin" value={fmtPct(inst.netInterestMargin, 2)} peer={peer ? fmtPct(peer.netInterestMargin, 2) : undefined}
              delta={inst.netInterestMargin != null && peer?.netInterestMargin != null ? inst.netInterestMargin - peer.netInterestMargin : undefined} suffix="%" />
            <SummaryCard label="Efficiency Ratio" value={fmtPct(inst.efficiencyRatio)} peer={peer ? fmtPct(peer.efficiencyRatio) : undefined}
              delta={inst.efficiencyRatio != null && peer?.efficiencyRatio != null ? inst.efficiencyRatio - peer.efficiencyRatio : undefined} suffix="%" invert />
            <SummaryCard label="Tier 1 Leverage" value={fmtPct(inst.tier1RbcRatio)} peer={peer ? fmtPct(peer.tier1RbcRatio) : undefined}
              delta={inst.tier1RbcRatio != null && peer?.tier1RbcRatio != null ? inst.tier1RbcRatio - peer.tier1RbcRatio : undefined} suffix="%" />
            <SummaryCard label="Loan-to-Deposit" value={fmtPct(inst.loanToDepositRatio)} peer={peer ? fmtPct(peer.loanToDepositRatio) : undefined} />
          </div>

          {/* Risk flags */}
          {riskFlags.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-center gap-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
                <h3 className="text-sm font-semibold text-amber-800">Risk Indicators</h3>
              </div>
              <ul className="mt-2 list-disc list-inside space-y-1">
                {riskFlags.map((flag, i: number) => (
                  <li key={i} className="text-sm text-amber-700">
                    <span className="font-medium">{flag.metric}:</span> {flag.message}
                    {flag.value != null && <span className="text-amber-600"> ({Number(flag.value).toFixed(2)})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Peer Comparison Table */}
          <div className="rounded-lg border bg-white">
            <div className="border-b px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-900">Institution vs. Peer Group</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Metric</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Your Bank</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Peer Avg</th>
                    <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Delta</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {[
                    { label: 'Total Assets ($000s)', key: 'totalAssets', format: 'dollar' as const },
                    { label: 'Total Deposits ($000s)', key: 'totalDeposits', format: 'dollar' as const },
                    { label: 'Net Loans & Leases ($000s)', key: 'netLoansLeases', format: 'dollar' as const },
                    { label: 'Total Equity ($000s)', key: 'totalEquity', format: 'dollar' as const },
                    { label: 'Return on Assets (%)', key: 'returnOnAssets', suffix: '%' },
                    { label: 'Return on Equity (%)', key: 'returnOnEquity', suffix: '%' },
                    { label: 'Net Interest Margin (%)', key: 'netInterestMargin', suffix: '%' },
                    { label: 'Efficiency Ratio (%)', key: 'efficiencyRatio', suffix: '%', invert: true },
                    { label: 'Net Charge-Off Rate (%)', key: 'netChargeOffRate', suffix: '%', invert: true },
                    { label: 'Past Due 30-89 Days (%)', key: 'pastDue30to89', suffix: '%', invert: true },
                    { label: 'Past Due 90+ Days (%)', key: 'pastDue90Plus', suffix: '%', invert: true },
                    { label: 'Tier 1 Leverage Ratio (%)', key: 'tier1RbcRatio', suffix: '%' },
                    { label: 'Total Risk-Based Capital (%)', key: 'totalRbcRatio', suffix: '%' },
                    { label: 'Loan-to-Deposit Ratio (%)', key: 'loanToDepositRatio', suffix: '%' },
                  ].map(row => {
                    const instVal = inst?.[row.key as keyof typeof inst] as number | undefined;
                    const peerVal = peer?.[row.key as keyof typeof peer] as number | undefined;
                    return (
                      <tr key={row.key} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-900">{row.label}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm font-medium text-gray-900">
                          {instVal != null ? (row.format === 'dollar' ? `$${instVal.toLocaleString()}` : `${instVal.toFixed(2)}${row.suffix || ''}`) : '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-500">
                          {peerVal != null ? (row.format === 'dollar' ? `$${peerVal.toLocaleString()}` : `${peerVal.toFixed(2)}${row.suffix || ''}`) : '—'}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm">
                          <MetricDelta value={instVal} peer={peerVal} format={row.format} suffix={row.suffix} invert={row.invert} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Quarterly Trend Table */}
          {crTrend?.quarters && crTrend.quarters.length > 0 && (
            <div className="rounded-lg border bg-white">
              <div className="border-b px-4 py-3">
                <h3 className="text-sm font-semibold text-gray-900">Quarterly Trend</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Quarter</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Assets ($M)</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">ROA %</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">NIM %</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Efficiency %</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Tier 1 %</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">NCO %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {crTrend.quarters.map((q: any) => (
                      <tr key={q.reportDate} className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-4 py-2.5 text-sm font-medium text-gray-900">
                          {new Date(q.reportDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short' })}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-900">
                          ${(q.totalAssets / 1000).toFixed(0)}M
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-900">{q.returnOnAssets?.toFixed(2)}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-900">{q.netInterestMargin?.toFixed(2)}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-900">{q.efficiencyRatio?.toFixed(1)}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-900">{q.tier1RbcRatio?.toFixed(1)}</td>
                        <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-900">{q.netChargeOffRate?.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Data freshness */}
          {crDash?.fetchedAt && (
            <p className="text-xs text-gray-400">Data last refreshed: {new Date(crDash.fetchedAt).toLocaleString()}</p>
          )}
        </>
      )}
    </div>
  );
}

// ─── Summary Card ─────────────────────────────────────────────────────────────

function SummaryCard({ label, value, peer, delta, suffix = '', invert }: {
  label: string; value: string; peer?: string; delta?: number; suffix?: string; invert?: boolean;
}) {
  const isGood = delta != null && (invert ? delta < 0 : delta > 0);
  const isBad = delta != null && (invert ? delta > 0 : delta < 0);
  return (
    <div className="rounded-lg border bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-xl font-bold text-gray-900">{value}</p>
      {peer && <p className="mt-0.5 text-xs text-gray-400">Peer: {peer}</p>}
      {delta != null && (
        <p className={clsx('mt-0.5 text-xs font-medium', isGood && 'text-green-600', isBad && 'text-red-600', !isGood && !isBad && 'text-gray-400')}>
          {delta > 0 ? '+' : ''}{delta.toFixed(2)}{suffix} vs peers
        </p>
      )}
    </div>
  );
}

// ─── FDIC Setup Form ──────────────────────────────────────────────────────────

function FdicSetupForm({ teamId, onComplete }: { teamId: string; onComplete: () => void }) {
  const [certNumber, setCertNumber] = useState('');
  const [peerState, setPeerState] = useState('KS');
  // Peer Asset Range hidden 2026-04-26 — fdicPeerAssetMin/Max omitted so backend skips peer-group fetch.
  // To restore: re-add `assetMin`/`assetMax` state + UI block (see git history) and pass them in the mutation.

  const saveMutation = useMutation({
    mutationFn: () => callReportApi.updateConfig(teamId, {
      fdicCertNumber: parseInt(certNumber, 10),
      fdicPeerState: peerState,
    }),
    onSuccess: () => {
      toast.success('FDIC configuration saved');
      onComplete();
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to save'),
  });

  return (
    <div className="mx-auto max-w-lg rounded-lg border bg-white p-6">
      <div className="text-center">
        <BanknotesIcon className="mx-auto h-12 w-12 text-blue-500" />
        <h2 className="mt-3 text-lg font-semibold text-gray-900">Configure FDIC Financial Metrics</h2>
        <p className="mt-1 text-sm text-gray-500">
          Enter your FDIC certificate number to pull Call Report data and compare against peer institutions.
        </p>
      </div>

      <div className="mt-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">FDIC Certificate Number</label>
          <input value={certNumber} onChange={e => setCertNumber(e.target.value)}
            type="number"
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder="e.g., 12345" />
          <p className="mt-1 text-xs text-gray-400">Find your cert number at research.fdic.gov</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Peer Group State</label>
          <select value={peerState} onChange={e => setPeerState(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
            {PEER_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {/* Min/Max Asset inputs hidden 2026-04-26 — Peer Asset Range tabled until Michael's reworked peer methodology lands. */}

        <button
          onClick={() => saveMutation.mutate()}
          disabled={!certNumber.trim() || saveMutation.isPending}
          className="w-full rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
        >
          {saveMutation.isPending ? 'Saving...' : 'Save & Fetch Data'}
        </button>
      </div>
    </div>
  );
}

// ─── HMDA Setup Form ──────────────────────────────────────────────────────────

function SetupForm({ teamId, onComplete }: { teamId?: string; onComplete: () => void }) {
  const [lei, setLei] = useState('');
  const [name, setName] = useState('');
  const [msaCodes, setMsaCodes] = useState('');
  const [assetBucket, setAssetBucket] = useState('200M-500M');

  const saveMutation = useMutation({
    mutationFn: () => peerBenchmarkingApi.updateConfig(teamId!, {
      institutionLei: lei || undefined,
      institutionName: name || undefined,
      msaCodes: msaCodes.split(',').map(s => s.trim()).filter(Boolean),
      assetSizeBucket: assetBucket,
      customPeerLeis: [],
      autoRefresh: true,
    }),
    onSuccess: () => {
      toast.success('Configuration saved');
      onComplete();
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to save'),
  });

  return (
    <div className="mx-auto max-w-lg rounded-lg border bg-white p-6">
      <div className="text-center">
        <ChartBarIcon className="mx-auto h-12 w-12 text-blue-500" />
        <h2 className="mt-3 text-lg font-semibold text-gray-900">Configure Peer Benchmarking</h2>
        <p className="mt-1 text-sm text-gray-500">Set up your institution profile to compare against peers.</p>
      </div>

      <div className="mt-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Institution LEI</label>
          <input value={lei} onChange={e => setLei(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder="20-character Legal Entity Identifier" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Institution Name</label>
          <input value={name} onChange={e => setName(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">MSA Codes (comma-separated)</label>
          <input value={msaCodes} onChange={e => setMsaCodes(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            placeholder="e.g., 12060, 33100, 47900" />
          <p className="mt-1 text-xs text-gray-400">Enter the MSA/MD codes for your markets</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Asset Size Bucket</label>
          <select value={assetBucket} onChange={e => setAssetBucket(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
            {ASSET_BUCKETS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </div>

        <button
          onClick={() => saveMutation.mutate()}
          disabled={!msaCodes.trim() || saveMutation.isPending}
          className="w-full rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
        >
          {saveMutation.isPending ? 'Saving...' : 'Save & Fetch Data'}
        </button>
      </div>
    </div>
  );
}

// ─── Config Modal ────────────────────────────────────────────────────────────

function ConfigModal({ isOpen, onClose, teamId, existingConfig }: {
  isOpen: boolean; onClose: () => void; teamId?: string; existingConfig?: any;
}) {
  const queryClient = useQueryClient();
  const [lei, setLei] = useState(existingConfig?.institutionLei || '');
  const [name, setName] = useState(existingConfig?.institutionName || '');
  const [msaCodes, setMsaCodes] = useState(existingConfig?.msaCodes?.join(', ') || '');
  const [assetBucket, setAssetBucket] = useState(existingConfig?.assetSizeBucket || 'SMALL');

  const saveMutation = useMutation({
    mutationFn: () => peerBenchmarkingApi.updateConfig(teamId!, {
      institutionLei: lei || undefined,
      institutionName: name || undefined,
      msaCodes: msaCodes.split(',').map((s: string) => s.trim()).filter(Boolean),
      assetSizeBucket: assetBucket,
      customPeerLeis: existingConfig?.customPeerLeis || [],
      autoRefresh: true,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['peerConfig'] });
      queryClient.invalidateQueries({ queryKey: ['peerDashboard'] });
      toast.success('Configuration updated');
      onClose();
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to update'),
  });

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
            <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <div className="flex items-center justify-between">
                <Dialog.Title className="text-lg font-semibold text-gray-900">Peer Benchmarking Config</Dialog.Title>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-5 w-5" /></button>
              </div>
              <div className="mt-4 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Institution LEI</label>
                  <input value={lei} onChange={e => setLei(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Institution Name</label>
                  <input value={name} onChange={e => setName(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">MSA Codes (comma-separated)</label>
                  <input value={msaCodes} onChange={e => setMsaCodes(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">CRA Asset Size</label>
                  <select value={assetBucket} onChange={e => setAssetBucket(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                    {ASSET_BUCKETS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={onClose} className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700">Cancel</button>
                <button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}
                  className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50">
                  {saveMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
